// Behaviour for sections/neuracoil-crystal-tone.liquid. Loaded with defer from inside the section, so document.currentScript.closest() finds its own instance.
(() => {
  const section = document.currentScript.closest('[data-neuracoil-crystal-tone]');
  if (!section) return;

  // Keep the effect's current frame, but stop its clock outside the viewport.
  let effectsInView = !('IntersectionObserver' in window);
  const syncEffects = () => {
    section.dataset.effectsActive = String(effectsInView && !document.hidden);
  };
  const effectsObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(([entry]) => {
      effectsInView = entry.isIntersecting;
      syncEffects();
      if (effectsInView) {
        scheduleConnectorUpdate();
        scheduleCrystalArrivalCheck();
      }
    })
    : null;
  effectsObserver?.observe(section);
  document.addEventListener('visibilitychange', syncEffects);
  const disposeEffects = (event) => {
    if (!event.target.contains(section)) return;
    effectsObserver?.disconnect();
    document.removeEventListener('visibilitychange', syncEffects);
    document.removeEventListener('shopify:section:unload', disposeEffects);
  };
  document.addEventListener('shopify:section:unload', disposeEffects);
  syncEffects();

  const callouts = Array.from(section.querySelectorAll('[data-neuracoil-crystal-callout]'));
  const buttons = callouts.map((callout) => callout.querySelector('.neuracoil-crystal-tone__callout-button')).filter(Boolean);
  const calloutsLayer = section.querySelector('[data-neuracoil-crystal-callouts]');
  const connectorsLayer = section.querySelector('[data-neuracoil-crystal-connectors]');
  // Refined stacks the callouts up to 989px, so tablets use the phone behaviour.
  const refined = section.classList.contains('neuracoil-crystal-tone--refined');
  const stackedQuery = window.matchMedia(refined ? '(max-width: 989px)' : '(max-width: 749px)');
  const copyBlock = refined ? section.querySelector('.neuracoil-crystal-tone__copy') : null;
  const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  const mobileInteractionQuery = window.matchMedia(`${stackedQuery.media}, (hover: none), (pointer: coarse)`);
  const defaultCallout = section.querySelector('[data-neuracoil-crystal-callout="stereo"]');
  const connectors = new Map(
    Array.from(section.querySelectorAll('[data-neuracoil-crystal-connector]')).map((connector) => [
      connector.dataset.neuracoilCrystalConnector,
      {
        line: connector.querySelector('.neuracoil-crystal-tone__connector-line'),
        path: connector.querySelector('.neuracoil-crystal-tone__connector-path'),
        dot: connector.querySelector('.neuracoil-crystal-tone__connector-dot'),
      },
    ])
  );
  let connectorAnimationFrame = null;
  let connectorAnimationUntil = 0;
  let hasReachedCrystalSection = false;
  let hasAutoOpenedDefaultCallout = false;
  let hasHoveredCallout = false;
  let crystalArrivalFrame = null;

  const getCssPixelValue = (element, property) => {
    const value = Number.parseFloat(getComputedStyle(element).getPropertyValue(property));
    return Number.isFinite(value) ? value : 0;
  };

  const setCssPixelValue = (element, property, value, currentValue) => {
    const stableValue = Math.abs(value) < 0.25 ? 0 : value;
    if (Math.abs(currentValue - stableValue) < 0.25) return;

    element.style.setProperty(property, `${stableValue}px`);
  };

  const rememberScrollPosition = (button) => {
    button.dataset.neuracoilScrollX = String(window.scrollX);
    button.dataset.neuracoilScrollY = String(window.scrollY);
  };

  const restoreScrollPosition = (scrollX, scrollY) => {
    const root = document.documentElement;
    const restore = () => {
      if (Math.abs(window.scrollX - scrollX) < 0.5 && Math.abs(window.scrollY - scrollY) < 0.5) return;

      const previousScrollBehavior = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      window.scrollTo(scrollX, scrollY);
      root.style.scrollBehavior = previousScrollBehavior;
    };

    restore();
  };

  const focusWithoutScroll = (element) => {
    try {
      element.focus({ preventScroll: true });
    } catch (error) {
      element.focus();
    }
  };

  const clearCalloutBoundaryOffsets = () => {
    callouts.forEach((callout) => {
      callout.style.setProperty('--card-boundary-x', '0px');
      callout.style.setProperty('--card-boundary-y', '0px');
    });
  };

  const constrainCallouts = () => {
    if (!calloutsLayer || stackedQuery.matches) return;

    const layerRect = calloutsLayer.getBoundingClientRect();
    const boundaryGap = Number.parseFloat(getComputedStyle(section).getPropertyValue('--crystal-callout-boundary-gap')) || 16;
    // Refined keeps open cards below the heading and subheading.
    const copyBottom = copyBlock ? copyBlock.getBoundingClientRect().bottom + boundaryGap : -Infinity;

    const corrections = callouts.map((callout) => {
      const calloutRect = callout.getBoundingClientRect();
      const minX = layerRect.left + boundaryGap;
      const maxX = layerRect.right - boundaryGap;
      const minY = Math.max(layerRect.top + boundaryGap, copyBottom);
      const maxY = layerRect.bottom - boundaryGap;
      const currentX = getCssPixelValue(callout, '--card-boundary-x');
      const currentY = getCssPixelValue(callout, '--card-boundary-y');
      let nudgeX = 0;
      let nudgeY = 0;

      if (calloutRect.left < minX) {
        nudgeX = minX - calloutRect.left;
      } else if (calloutRect.right > maxX) {
        nudgeX = maxX - calloutRect.right;
      }

      if (calloutRect.top < minY) {
        nudgeY = minY - calloutRect.top;
      } else if (calloutRect.bottom > maxY) {
        nudgeY = maxY - calloutRect.bottom;
      }

      return { callout, currentX, currentY, nudgeX, nudgeY };
    });
    corrections.forEach(({ callout, currentX, currentY, nudgeX, nudgeY }) => {
      setCssPixelValue(callout, '--card-boundary-x', currentX + nudgeX, currentX);
      setCssPixelValue(callout, '--card-boundary-y', currentY + nudgeY, currentY);
    });
  };

  const updateConnectors = () => {
    if (!connectorsLayer || !effectsInView || document.hidden) return;

    constrainCallouts();

    const layerRect = connectorsLayer.getBoundingClientRect();
    // Measure all callouts first; SVG/style writes otherwise force a layout per connector.
    const updates = callouts.map((callout) => {
      const connector = connectors.get(callout.dataset.neuracoilCrystalCallout);
      if (!connector?.line || !connector?.path || !connector?.dot) return;

      const calloutRect = callout.getBoundingClientRect();
      const anchorSide = callout.dataset.neuracoilCrystalAnchor === 'left' ? 'left' : 'right';
      const anchorY = Number.parseFloat(callout.dataset.neuracoilCrystalAnchorY || '0.5');
      const targetX = Number.parseFloat(callout.dataset.neuracoilCrystalTargetX || '50');
      const targetY = Number.parseFloat(callout.dataset.neuracoilCrystalTargetY || '50');
      const cardJoinOffset = 0.8;
      const startX = (anchorSide === 'left' ? calloutRect.left : calloutRect.right) - layerRect.left + (anchorSide === 'left' ? -cardJoinOffset : cardJoinOffset);
      const startY = calloutRect.top - layerRect.top + (calloutRect.height * anchorY);
      const endX = layerRect.width * (targetX / 100);
      const endY = layerRect.height * (targetY / 100);
      const deltaX = endX - startX;
      const deltaY = endY - startY;
      const distance = Math.hypot(deltaX, deltaY);
      const angle = Math.atan2(deltaY, deltaX);
      const dotSize = Number.parseFloat(getComputedStyle(connector.dot).width) || 8;
      const lineDistance = Math.max(0, distance - (dotSize / 2) - 1);
      const lineEndX = startX + Math.cos(angle) * lineDistance;
      const lineEndY = startY + Math.sin(angle) * lineDistance;
      const handle = Math.min(Math.max(distance * 0.34, 48), 150);
      const side = anchorSide === 'left' ? -1 : 1;
      const controlOneX = startX + (handle * side);
      const controlOneY = startY + (deltaY * 0.08);
      const controlTwoX = lineEndX - (handle * side * 0.56);
      const controlTwoY = lineEndY - (deltaY * 0.12);

      return () => {
        connector.line.setAttribute('viewBox', `0 0 ${layerRect.width} ${layerRect.height}`);
        connector.path.setAttribute(
          'd',
          `M ${startX} ${startY} C ${controlOneX} ${controlOneY}, ${controlTwoX} ${controlTwoY}, ${lineEndX} ${lineEndY}`
        );
        connector.dot.style.left = `${endX}px`;
        connector.dot.style.top = `${endY}px`;
      };
    });
    updates.forEach(update => update?.());
  };

  let connectorUpdateFrame = null;
  let connectorUpdateTimer = null;
  const scheduleConnectorUpdate = () => {
    if (!effectsInView || document.hidden) return;
    if (connectorUpdateFrame === null) connectorUpdateFrame = window.requestAnimationFrame(() => {
      connectorUpdateFrame = null;
      updateConnectors();
    });
    window.clearTimeout(connectorUpdateTimer);
    connectorUpdateTimer = window.setTimeout(updateConnectors, 280);
  };

  const trackConnectorTransition = (duration = 340) => {
    connectorAnimationUntil = Math.max(connectorAnimationUntil, window.performance.now() + duration);
    if (connectorAnimationFrame !== null) return;

    const tick = () => {
      if (!effectsInView || document.hidden) { connectorAnimationFrame = null; return; }
      updateConnectors();

      if (window.performance.now() < connectorAnimationUntil) {
        connectorAnimationFrame = window.requestAnimationFrame(tick);
        return;
      }

      connectorAnimationFrame = null;
      updateConnectors();
    };

    connectorAnimationFrame = window.requestAnimationFrame(tick);
  };

  const resetCallouts = () => {
    section.removeAttribute('data-neuracoil-crystal-active');
    section.classList.remove('has-active-callout');
    clearCalloutBoundaryOffsets();
    callouts.forEach((callout) => {
      callout.classList.remove('is-active');
      callout.querySelector('.neuracoil-crystal-tone__callout-button')?.setAttribute('aria-expanded', 'false');
    });
    trackConnectorTransition();
  };

  const activateCallout = (activeCallout) => {
    const activeId = activeCallout.dataset.neuracoilCrystalCallout;
    clearCalloutBoundaryOffsets();
    section.dataset.neuracoilCrystalActive = activeId;
    section.classList.add('has-active-callout');

    callouts.forEach((callout) => {
      const isActive = callout === activeCallout;
      callout.classList.toggle('is-active', isActive);
      callout.querySelector('.neuracoil-crystal-tone__callout-button')?.setAttribute('aria-expanded', String(isActive));
    });
    trackConnectorTransition();
  };

  const canUseHoverActivation = () => hoverQuery.matches && !mobileInteractionQuery.matches;

  const isAnyCalloutHovered = () => canUseHoverActivation() && callouts.some((callout) => callout.matches(':hover'));

  const activateDefaultCalloutIfIdle = () => {
    if (
      !defaultCallout ||
      !canUseHoverActivation() ||
      !hasReachedCrystalSection ||
      hasAutoOpenedDefaultCallout ||
      hasHoveredCallout ||
      isAnyCalloutHovered()
    ) return;

    hasAutoOpenedDefaultCallout = true;
    activateCallout(defaultCallout);
  };

  const markCrystalSectionReached = () => {
    if (hasReachedCrystalSection) return;

    hasReachedCrystalSection = true;
    section.dataset.neuracoilCrystalReached = 'true';
    window.removeEventListener('scroll', scheduleCrystalArrivalCheck);
    window.removeEventListener('resize', scheduleCrystalArrivalCheck);
    window.requestAnimationFrame(activateDefaultCalloutIfIdle);
    window.setTimeout(activateDefaultCalloutIfIdle, 120);
  };

  const checkCrystalArrival = () => {
    crystalArrivalFrame = null;
    if (hasReachedCrystalSection) return;

    const rect = section.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const headerBottom = Math.max(0, document.querySelector('#header-component')?.getBoundingClientRect().bottom || 0);
    const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
    const possibleVisibleHeight = Math.max(1, Math.min(rect.height, viewportHeight));
    const sectionFill = visibleHeight / possibleVisibleHeight;
    const topHasSettled = rect.top <= headerBottom + 24;
    const sectionStillCarriesViewport = rect.bottom >= viewportHeight * 0.58;

    if (sectionFill >= 0.82 && topHasSettled && sectionStillCarriesViewport) {
      markCrystalSectionReached();
    }
  };

  const scheduleCrystalArrivalCheck = () => {
    if (crystalArrivalFrame !== null || !effectsInView || document.hidden) return;

    crystalArrivalFrame = window.requestAnimationFrame(checkCrystalArrival);
  };

  callouts.forEach((callout) => {
    callout.addEventListener('pointerenter', (event) => {
      if (!canUseHoverActivation() || event.pointerType === 'touch') return;

      hasHoveredCallout = true;
      activateCallout(callout);
    });

    callout.addEventListener('pointerleave', (event) => {
      if (!canUseHoverActivation() || event.pointerType === 'touch') return;
      if (!callout.classList.contains('is-active')) return;

      window.requestAnimationFrame(() => {
        if (isAnyCalloutHovered()) return;

        resetCallouts();
      });
    });
  });

  buttons.forEach((button) => {
    button.addEventListener('pointerdown', () => {
      rememberScrollPosition(button);
    }, { passive: true });

    button.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        rememberScrollPosition(button);
      }
    });

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const rememberedScrollX = Number.parseFloat(button.dataset.neuracoilScrollX);
      const rememberedScrollY = Number.parseFloat(button.dataset.neuracoilScrollY);
      const scrollX = Number.isFinite(rememberedScrollX) ? rememberedScrollX : window.scrollX;
      const scrollY = Number.isFinite(rememberedScrollY) ? rememberedScrollY : window.scrollY;
      const callout = button.closest('[data-neuracoil-crystal-callout]');
      if (!callout) return;

      if (canUseHoverActivation() && event.detail !== 0) {
        activateCallout(callout);
        focusWithoutScroll(button);
        restoreScrollPosition(scrollX, scrollY);
        return;
      }

      if (callout.classList.contains('is-active')) {
        resetCallouts();
        focusWithoutScroll(button);
        restoreScrollPosition(scrollX, scrollY);
        return;
      }

      activateCallout(callout);
      focusWithoutScroll(button);
      restoreScrollPosition(scrollX, scrollY);
    });
  });

  document.addEventListener('click', (event) => {
    if (!section.classList.contains('has-active-callout')) return;
    if (event.target.closest('[data-neuracoil-crystal-callout]')) return;
    resetCallouts();
  });

  section.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      resetCallouts();
    }
  });

  callouts.forEach((callout) => {
    callout.addEventListener('transitionend', updateConnectors);
  });

  window.addEventListener('resize', scheduleConnectorUpdate, { passive: true });
  window.addEventListener('resize', scheduleCrystalArrivalCheck, { passive: true });
  window.addEventListener('scroll', scheduleCrystalArrivalCheck, { passive: true });

  if ('ResizeObserver' in window) {
    const resizeObserver = new ResizeObserver(scheduleConnectorUpdate);
    resizeObserver.observe(section);
  }

  const revealSection = () => {
    section.classList.add('is-revealed');
    trackConnectorTransition(640);
  };

  if ('IntersectionObserver' in window) {
    const revealObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      revealSection();
      revealObserver.disconnect();
    }, { threshold: 0.18 });
    revealObserver.observe(section);
  } else {
    revealSection();
  }

  scheduleConnectorUpdate();
  scheduleCrystalArrivalCheck();
})();
